import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { 
  matPhone,
  matLocationOn
} from '@ng-icons/material-icons/baseline';
import { 
  matCloseOutline,
  matSettingsOutline,
  matMessageOutline,
  matNotificationsOutline,
  matAddOutline,
  matDeleteOutline,
  matPaymentOutline,
  matLockOutline
} from '@ng-icons/material-icons/outline';
import { CountryService } from '../../services/country-service/country.service';
import { DistrictService } from '../../services/district-service/district.service';
import { Country } from '../../../../platform/interfaces/country';
import { District } from '../../../../platform/interfaces/district';
import { Unit } from '../../interfaces';
import { Configuration, DEFAULT_NOTICE_OF_COLLECTION_MESSAGE } from '../../interfaces/configuration';
import { ConfigurationService } from '../../services/configuration-service/configuration.service';
import { environment } from '../../../../../environments/environment';
import { UserService } from '../../../../platform/services/user-service/userservice.service';
import PermissionsUtil from '../../../../common/util/permissions-util';
import { AppDataState } from '../../../../common/state-stores/app-data-state.state';
import { AppInstanceService } from '../../../../platform/services/app-instance-service/app-instance-service.service';
import { ModalState } from '../../../../common/state-stores/modal-state.state';
import { MonerisPaymentService } from '../../services/moneris-payment-service/moneris-payment.service';
import { MonerisConfiguration } from '../../interfaces/moneris-payment';
import { WorldlinePaymentService } from '../../services/worldline-payment-service/worldline-payment.service';
import { WorldlineConfiguration } from '../../interfaces/worldline-payment';
import { DateTimeSettingsState, WINDOWS_TIMEZONE_OPTIONS, WINDOWS_TO_IANA } from '../../../../common/state-stores/date-time-settings.state';
import { OperatorService } from '../../../../platform/services/operator-service/operator.service';
import ObjectUtil from '../../../../common/util/object-util';
import { SearchableSelectComponent } from '../../../../common/components/searchable-select/searchable-select.component';
import { FieldConfig } from '../../../../common/components/dynamicform/dynamicform.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-configuration',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    NgIconComponent,
    SearchableSelectComponent
  ],
  providers: [
    provideIcons({
      matCloseOutline,
      matPhone,
      matLocationOn,
      matSettingsOutline,
      matMessageOutline,
      matNotificationsOutline,
      matAddOutline,
      matDeleteOutline,
      matPaymentOutline,
      matLockOutline
    })
  ],
  templateUrl: './configuration-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./configuration-modal.component.css']
})
export class ConfigurationModalComponent implements OnInit, OnDestroy {
  @Input() configurationId?: number;
  @Input() units: Unit[] = [];
  @Output() close = new EventEmitter<void>();

  configuration: Configuration = {
    name: '',
    billingFactor: 1,
    smsEnable: false,
    notificationsEnabled: false,
    unitId: 0,
    adminMFAEnabled: false,
    customerMFAEnabled: false,
    noticeOfCollectionEnabled: false,
    noticeOfCollectionMessage: '',
    noticeOfCollectionRequireAgreement: false,
    adminMFAMethods: [],
    customerMFAMethods: [],
    hideFinancialDataForOperator: false,
    hideFinancialDataForCustomer: false,
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      postalCode: ''
    },
    smsNumbers: [],
    notificationEmails: []
  };

  countries: Country[] = [];
  districts: District[] = [];
  selectedCountryId?: number;
  districtLabel: string = 'District';
  postalCodeLabel: string = 'Postal Code';

  isLoading = false;
  isSaving = false;
  isEditMode = false;
  hasUnsavedChanges = false;
  originalConfiguration: string = '';
  showUnsavedWarning = false;
  showSuccessMessage = false;
  private shouldRedirectPrepaymentToHomeOnClose = false;
  
  // Tab management
  activeTab: number = 0;
  dropdownOpen: boolean = false;
  tabs: { name: string; icon: string, permission?: string | string[], module?: string | string[], superUserAccess?: boolean }[] = [];
  isSuperUser: boolean = false;
  
  // All available tabs
  private allTabs : { name: string; icon: string, permission?: string | string[], module?: string | string[], superUserAccess?: boolean }[] = [
    { name: 'General', icon: 'matSettingsOutline' },
    { name: 'SMS Settings', icon: 'matMessageOutline' },
    { name: 'Security Settings', icon: 'matLockOutline' },
    { name: 'Notification Settings', icon: 'matNotificationsOutline' },
    { name: 'Payment Settings', icon: 'matPaymentOutline', permission: ['Moneris.ProcessPayment', 'Worldline.ProcessPayment'], module: ['Moneris Payment', 'Worldline Payment'] },
    { name: 'Sync Service', icon: 'matSyncOutline' }
  ];
  
  // Payment settings
  monerisEnvironmentMode: 'test' | 'production' = environment.production ? 'production' : 'test';
  monerisConfiguration: MonerisConfiguration = {
    storeId: '',
    apiToken: '',
    hostedTokenizationProfileId: '',
    testEnvironment: true
  };
  showApiToken: boolean = false;

  worldlineConfiguration: WorldlineConfiguration = {
    merchantId: '',
    paymentsApiKey: ''
  };
  showWorldlinePaymentsApiKey: boolean = false;
  hasMonerisModule: boolean = false;
  hasWorldlineModule: boolean = false;

  // Keep stable object identities for email row editing to avoid input focus resets.
  notificationEmailRows: { value: string }[] = [];

  // Logo
  logoPreviewUrl: string | null = null;
  logoFile: File | null = null;
  logoBase64: string | null = null;
  logoChanged: boolean = false;

  /** Timezone options for the select dropdown (Windows display names → IANA values). */
  readonly timezoneOptions = WINDOWS_TIMEZONE_OPTIONS;
  /** Timezone options annotated with their current GMT offset, for the searchable select. */
  private timezoneOptionsWithOffset: { label: string; value: string; searchTerm: string }[] = [];
  timezoneSelectField: FieldConfig = {} as FieldConfig;

  constructor(
    private configurationService: ConfigurationService,
    private countryService: CountryService,
    private districtService: DistrictService,
    private userService: UserService,
    private appDataState: AppDataState,
    private appInstanceService: AppInstanceService,
    private modalState: ModalState,
    private monerisPaymentService: MonerisPaymentService,
    private worldlinePaymentService: WorldlinePaymentService,
    private dateTimeSettings: DateTimeSettingsState,
    private operatorService: OperatorService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {}

  async ngOnInit() {
    // Set modal open state
    this.modalState.setModalOpen(true);
    
    this.isEditMode = true;
    
    // Load user permissions and filter tabs
    await this.loadTabsBasedOnPermissions();
    
    // Load Moneris environment preference from localStorage (overrides environment default)
    const savedMode = localStorage.getItem('monerisEnvironmentMode');
    if (savedMode === 'production' || savedMode === 'test') {
      this.monerisEnvironmentMode = savedMode;
    }
    // If no localStorage value, keep the default from environment.production
    
    await this.loadConfiguration();
    this.initTimezoneSelectField();
    await this.loadMonerisConfiguration();
    await this.loadOperatorLogo();

    // Load units if not provided
    if (!this.units || this.units.length === 0) {
      await this.loadUnits();
    }

    // Load countries
    await this.loadCountries();

    // Load districts if countryId is set
    if (this.configuration.address?.countryId) {
      this.selectedCountryId = this.configuration.address.countryId;
      const selectedCountry = this.countries.find(c => c.id === this.selectedCountryId);
      this.districtLabel = selectedCountry?.districtType?.name || 'District';
      this.postalCodeLabel = selectedCountry?.postalCodeType?.name || 'Postal Code';
      await this.loadDistricts(this.selectedCountryId);
    }
    
    this.originalConfiguration = JSON.stringify(this.configuration);
  }

  ngOnDestroy(): void {
    // Clear modal open state
    this.modalState.setModalOpen(false);
  }
  
  private async loadTabsBasedOnPermissions() {
    try {
      // Get user permissions
      const permissions = await PermissionsUtil.getPermissionSets(this.userService, false);
      const appName = this.appDataState.getAppName() ?? 'StationApp';
      const operatorId = this.appDataState.getOperatorId();
      const permissionSetId = this.appDataState.getPermissionSetId();
      const userPermissions = permissions.get(`${appName}-${operatorId}-${permissionSetId}`)?.permissions || [];
      this.isSuperUser = userPermissions.includes('SuperUser');
      
      // Get app instances to check module availability
      const appInstances = await this.appInstanceService.getAppInstances();
      const appInstance = appInstances.find(
        (instance) => instance.id === this.appDataState.getAppInstanceId()
      );
      
      // Filter tabs based on permissions and module existence
      this.tabs = this.allTabs.filter(tab => {
        // Check permission requirement
        if (tab.permission) {
          const requiredPermissions = Array.isArray(tab.permission) ? tab.permission : [tab.permission];
          const hasPermission = requiredPermissions.some(permission =>
            PermissionsUtil.checkPermissions(
              [permission],
              userPermissions,
              tab.superUserAccess ?? true
            )
          );
          if (!hasPermission) return false;
        }
        
        // Check module requirement
        if (tab.module) {
          if (!appInstance) return false;
          const requiredModules = Array.isArray(tab.module) ? tab.module : [tab.module];
          const moduleExists = requiredModules.some(moduleName =>
            appInstance.modules?.find((mod) => mod.name === moduleName) !== undefined
          );
          if (!moduleExists) return false;
        }
        
        return true;
      });

      this.hasMonerisModule = appInstance?.modules?.some((mod) => mod.name === 'Moneris Payment') ?? false;
      this.hasWorldlineModule = appInstance?.modules?.some((mod) => mod.name === 'Worldline Payment') ?? false;
    } catch (error) {
      console.error('Error loading permissions:', error);
      // Fallback: show only General and SMS Settings tabs if permission check fails
      this.tabs = this.allTabs.filter(tab => !tab.permission && !tab.module);
    }
  }

  private async loadUnits() {
    try {
      const fetchedUnits = await this.configurationService.getAllUnits();
      // Coerce ids to number so they match configuration.unitId (GraphQL may return ID as string)
      this.units = fetchedUnits.map(unit => ({ ...unit, id: Number(unit.id) }));
    } catch (error) {
      console.error('Error loading units:', error);
      // Fallback to mock data if service fails
      this.units = [
        { id: 1, name: 'U.S. Gal', description: 'US Gallons' },
        { id: 2, name: 'm³', description: 'Cubic Meters' },
        { id: 3, name: 'Liter', description: 'Liters' }
      ];
    }

    if (this.configuration.unitId != null) {
      this.configuration.unitId = Number(this.configuration.unitId);
    }
  }

  /** Builds (and caches) timezone options annotated with their current GMT offset. */
  private getTimezoneOptionsWithOffset(): { label: string; value: string; searchTerm: string }[] {
    if (this.timezoneOptionsWithOffset.length === 0) {
      this.timezoneOptionsWithOffset = this.timezoneOptions.map(opt => {
        const displayLabel = `${opt.label} (${this.getGmtOffsetLabel(opt.value)})`;
        return { label: displayLabel, value: opt.label, searchTerm: displayLabel };
      });
    }
    return this.timezoneOptionsWithOffset;
  }

  private getGmtOffsetLabel(ianaZone: string): string {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: ianaZone, timeZoneName: 'shortOffset' }).formatToParts(new Date());
      return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
    } catch {
      return '';
    }
  }

  private initTimezoneSelectField() {
    const allOptions = this.getTimezoneOptionsWithOffset();
    const selected = allOptions.find(o => o.value === this.configuration.timeZone);

    this.timezoneSelectField = {
      name: 'timeZoneSelect',
      type: 'searchable-select',
      label: 'Timezone',
      value: this.configuration.timeZone,
      validations: [],
      options: allOptions,
      searchTerm: selected?.searchTerm ?? '',
      loading: false,
      listOpen: false,
      onSearchTermChange: (event: Event) => {
        const term = ((event.target as HTMLInputElement).value ?? '').toLowerCase();
        this.timezoneSelectField.options = term
          ? allOptions.filter(o => o.searchTerm.toLowerCase().includes(term))
          : allOptions;
      },
      onChange: (value: string | number) => {
        // The shared searchable-select fires onChange(0) on focus/clear - ignore it so it doesn't wipe a valid selection.
        if (value === 0) {
          return;
        }
        this.configuration.timeZone = value as string;
        const match = allOptions.find(o => o.value === value);
        this.timezoneSelectField.searchTerm = match?.searchTerm ?? '';
        this.checkForChanges();
      },
    } as FieldConfig;
  }

  private async loadCountries() {
    try {
      const response = await this.countryService.get();
      this.countries = response.data;
    } catch (error) {
      console.error('Error loading countries:', error);
      this.countries = [];
    }
  }

  private async loadDistricts(countryId: number) {
    try {
      this.districts = await this.districtService.getByCountryId(countryId);
    } catch (error) {
      console.error('Error loading districts:', error);
      this.districts = [];
    }
  }

  async onCountryChange(countryId: number) {
    this.selectedCountryId = countryId;
    const selectedCountry = this.countries.find(c => c.id === countryId);
    this.districtLabel = selectedCountry?.districtType?.name || 'District';
    this.postalCodeLabel = selectedCountry?.postalCodeType?.name || 'Postal Code';
    if (!this.configuration.address) {
      this.configuration.address = {
        street1: '',
        street2: '',
        city: '',
        state: '',
        postalCode: ''
      };
    }
    this.configuration.address.countryId = countryId;
    this.configuration.address.districtId = undefined; // Reset district when country changes
    await this.loadDistricts(countryId);
    this.checkForChanges();
  }

  private async loadConfiguration() {
    try {
      this.isLoading = true;
      const response = await this.configurationService.get();
      if (response && response.data.length > 0) {
        this.configuration = response.data[0];
        this.configuration.smsNumbers = this.configuration.smsNumbers ?? [];
        this.configuration.notificationEmails = this.configuration.notificationEmails ?? [];
        this.configuration.notificationsEnabled = this.configuration.notificationsEnabled ?? false;
        this.configuration.noticeOfCollectionEnabled = this.configuration.noticeOfCollectionEnabled ?? false;
        this.configuration.noticeOfCollectionMessage = this.configuration.noticeOfCollectionMessage ?? '';
        this.configuration.noticeOfCollectionRequireAgreement = this.configuration.noticeOfCollectionRequireAgreement ?? false;
        this.appDataState.setHideFinancialDataForOperator(this.configuration.hideFinancialDataForOperator ?? false);
        this.appDataState.setHideFinancialDataForCustomer(this.configuration.hideFinancialDataForCustomer ?? false);
        this.notificationEmailRows = this.configuration.notificationEmails.map((email) => ({ value: email }));
        // Apply the timezone from config to the app-wide DateTimeSettingsState.
        // The backend sends a Windows timezone name; map it to IANA for date-fns.
        if (this.configuration.timeZone) {
          const iana = WINDOWS_TO_IANA[this.configuration.timeZone] ?? this.configuration.timeZone;
          this.dateTimeSettings.setTimezone(iana);
        }
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      alert('Error loading configuration. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  private base64ToDataUrl(base64: string): string {
    return ObjectUtil.base64ToDataUrl(base64);
  }

  private async loadOperatorLogo() {
    // Pre-populate from cached state immediately so preview shows on re-open
    const cached = this.appDataState.getOperatorLogo();
    if (cached) {
      this.logoBase64 = cached;
      this.logoPreviewUrl = ObjectUtil.base64ToDataUrl(cached);
      this.cdr.markForCheck();
    }

    try {
      const operatorId = this.appDataState.getOperatorId();
      const operator = await this.operatorService.getById(operatorId);
      const base64 = ObjectUtil.logoToBase64(operator?.logo);
      if (base64) {
        this.logoBase64 = base64;
        this.logoPreviewUrl = ObjectUtil.base64ToDataUrl(base64);
        this.appDataState.setOperatorLogo(base64);
        this.cdr.markForCheck();
      } else {
        this.appDataState.setOperatorLogo(null);
      }
    } catch (error) {
      console.error('Error loading operator logo:', error);
    }
  }

  private async loadMonerisConfiguration() {
    // Only load if Payment Settings tab is available
    const hasPaymentTab = this.tabs.some(tab => tab.name === 'Payment Settings');
    if (!hasPaymentTab) return;

    if (this.hasMonerisModule) {
      try {
        this.monerisConfiguration = await this.monerisPaymentService.getConfiguration();
        // Update environment mode based on loaded configuration
        this.monerisEnvironmentMode = this.monerisConfiguration.testEnvironment ? 'test' : 'production';
      } catch (error) {
        console.error('Error loading Moneris configuration:', error);
        // Keep default values if loading fails
      }
    }

    if (this.hasWorldlineModule) {
      try {
        this.worldlineConfiguration = await this.worldlinePaymentService.getConfiguration();
      } catch (error) {
        console.error('Error loading Worldline configuration:', error);
        // Keep default values if loading fails
      }
    }
  }

  checkForChanges(): void {
    const currentConfiguration = JSON.stringify(this.configuration);
    this.hasUnsavedChanges = currentConfiguration !== this.originalConfiguration;
  }

  isMfaMethodSelected(methods: string[] | undefined, method: 'SMS' | 'EMAIL'): boolean {
    return !!methods?.includes(method);
  }

  /** MFA is enabled for a role precisely when at least one delivery method is selected for it -
   * there is no separate enable/disable toggle anymore. */
  isMfaEnabled(target: 'adminMFAMethods' | 'customerMFAMethods'): boolean {
    return (this.configuration[target]?.length ?? 0) > 0;
  }

  toggleMfaMethod(target: 'adminMFAMethods' | 'customerMFAMethods', method: 'SMS' | 'EMAIL'): void {
    const methods = this.configuration[target] ?? [];
    const updatedMethods = methods.includes(method)
      ? methods.filter((m) => m !== method)
      : [...methods, method];
    this.configuration[target] = updatedMethods;

    // Keep the enabled flag in lockstep with whether any method is selected, since the backend
    // still expects an explicit adminMFAEnabled/customerMFAEnabled boolean alongside the methods.
    const enabledKey = target === 'adminMFAMethods' ? 'adminMFAEnabled' : 'customerMFAEnabled';
    this.configuration[enabledKey] = updatedMethods.length > 0;

    this.checkForChanges();
  }

  addSMSNumber(): void {
    this.configuration.smsNumbers.push({
      number: ''
    });
    this.checkForChanges();
  }

  removeSMSNumber(index: number): void {
    this.configuration.smsNumbers.splice(index, 1);
    this.checkForChanges();
  }

  addNotificationEmail(): void {
    this.notificationEmailRows.push({ value: '' });
    this.syncNotificationEmails();
    this.checkForChanges();
  }

  removeNotificationEmail(index: number): void {
    this.notificationEmailRows.splice(index, 1);
    this.syncNotificationEmails();
    this.checkForChanges();
  }

  onNotificationEmailChange(): void {
    this.syncNotificationEmails();
    this.checkForChanges();
  }

  private syncNotificationEmails(): void {
    this.configuration.notificationEmails = this.notificationEmailRows.map((row) => row.value);
  }

  onNoticeOfCollectionEnabledChange(): void {
    // Pre-fill the standard legal text the first time the feature is turned on, so the operator
    // has a sensible starting point to edit rather than an empty textarea.
    if (this.configuration.noticeOfCollectionEnabled && !this.configuration.noticeOfCollectionMessage?.trim()) {
      this.configuration.noticeOfCollectionMessage = DEFAULT_NOTICE_OF_COLLECTION_MESSAGE;
    }
    this.checkForChanges();
  }

  async onSave() {
    try {
      const previousHideFinancialDataForOperator = this.appDataState.getHideFinancialDataForOperator();

      // Validate form
      if (!this.configuration.name || !this.configuration.unitId) {
        alert('Please fill in all required fields');
        return;
      }

      if (!this.configuration.timeZone) {
        alert('Timezone is required');
        return;
      }

      if (this.configuration.noticeOfCollectionEnabled && !this.configuration.noticeOfCollectionMessage?.trim()) {
        alert('Notice of Collection message is required when the notice is enabled');
        return;
      }

      if (!this.configuration.address.street1 || !this.configuration.address.city || !this.configuration.address.postalCode) {
        alert('Please fill in all required address fields');
        return;
      }

      if (!this.configuration.address.countryId) {
        alert('Country is required');
        return;
      }

      if (!this.configuration.address.districtId) {
        alert(`${this.districtLabel} is required`);
        return;
      }

      const selectedCountryId = Number(this.configuration.address.countryId);
      const selectedDistrictId = Number(this.configuration.address.districtId);
      const countryDistricts =
        this.selectedCountryId === selectedCountryId && this.districts.length > 0
          ? this.districts
          : await this.districtService.getByCountryId(selectedCountryId);
      const isDistrictValidForCountry = countryDistricts.some(
        (district) => district.id === selectedDistrictId,
      );
      if (!isDistrictValidForCountry) {
        alert(`${this.districtLabel} is required`);
        return;
      }

      // Validate SMS numbers if SMS is enabled
      if (this.configuration.smsEnable && this.configuration.smsNumbers.length > 0) {
        const invalidNumbers = this.configuration.smsNumbers.filter(sms => !sms.number || sms.number.length !== 10);
        if (invalidNumbers.length > 0) {
          alert('All SMS numbers must be exactly 10 digits');
          return;
        }
      }

      // Validate notification emails if notifications are enabled
      if (this.configuration.notificationsEnabled) {
        const notificationEmails = this.configuration.notificationEmails ?? [];
        const invalidEmails = notificationEmails.filter(email => !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
        if (invalidEmails.length > 0) {
          alert('All notification emails must be valid email addresses');
          return;
        }
      }

      // Show loading spinner
      this.isLoading = true;
      this.isSaving = true;

      // Save configuration via service
      if (this.isEditMode) {
        await this.configurationService.update(this.configuration);
      } else {
        const created = await this.configurationService.create(this.configuration);
        // Update configuration with returned data (includes ID)
        this.configuration = created;
      }

      // Save Moneris/Worldline configuration when Payment Settings tab is available and the
      // corresponding module is present on the app instance
      const hasPaymentTab = this.tabs.some(tab => tab.name === 'Payment Settings');
      if (hasPaymentTab) {
        if (this.hasMonerisModule) {
          // Update testEnvironment based on mode
          this.monerisConfiguration.testEnvironment = this.monerisEnvironmentMode === 'test';
          await this.monerisPaymentService.saveConfiguration(this.monerisConfiguration);
        }
        if (this.hasWorldlineModule) {
          await this.worldlinePaymentService.saveConfiguration(this.worldlineConfiguration);
        }
      }

      // Save operator logo if changed
      if (this.logoChanged) {
        await this.saveOperatorLogo();
      }

      // Show success message
      this.showSuccessMessage = true;
      this.appDataState.setHideFinancialDataForOperator(this.configuration.hideFinancialDataForOperator ?? false);
      this.appDataState.setHideFinancialDataForCustomer(this.configuration.hideFinancialDataForCustomer ?? false);
      this.shouldRedirectPrepaymentToHomeOnClose =
        previousHideFinancialDataForOperator !== (this.configuration.hideFinancialDataForOperator ?? false) &&
        this.isOnPrepaymentRoute();

      // Apply updated timezone app-wide.
      if (this.configuration.timeZone) {
        const iana = WINDOWS_TO_IANA[this.configuration.timeZone] ?? this.configuration.timeZone;
        this.dateTimeSettings.setTimezone(iana);
      }
      
      // Update original configuration to reflect saved state
      this.originalConfiguration = JSON.stringify(this.configuration);
      this.hasUnsavedChanges = false;
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      alert('Error saving configuration. Please try again.');
    } finally {
      // Hide loading spinner
      this.isLoading = false;
      this.isSaving = false;
    }
  }

  closeSuccessMessage() {
    this.showSuccessMessage = false;
  }

  onCancel() {
    if (this.hasUnsavedChanges) {
      this.showUnsavedWarning = true;
    } else {
      this.closeModal();
    }
  }

  confirmCancel() {
    this.showUnsavedWarning = false;
    this.closeModal();
  }

  dismissCancelWarning() {
    this.showUnsavedWarning = false;
  }

  private closeModal(): void {
    this.close.emit();

    if (!this.shouldRedirectPrepaymentToHomeOnClose) {
      return;
    }

    this.shouldRedirectPrepaymentToHomeOnClose = false;
    this.router.navigate(['/stationapp/home']);
  }

  private isOnPrepaymentRoute(): boolean {
    const pathWithoutQuery = this.router.url.split('?')[0].toLowerCase();
    return pathWithoutQuery.startsWith('/stationapp/billing/prepayment');
  }
  
  selectTab(index: number) {
    this.activeTab = index;
  }

  trackByIndex(index: number): number {
    return index;
  }
  
  toggleMonerisEnvironment() {
    this.monerisEnvironmentMode = this.monerisEnvironmentMode === 'test' ? 'production' : 'test';
    localStorage.setItem('monerisEnvironmentMode', this.monerisEnvironmentMode);
    this.checkForChanges();
  }
  
  toggleApiTokenVisibility() {
    this.showApiToken = !this.showApiToken;
  }

  toggleWorldlinePaymentsApiKeyVisibility() {
    this.showWorldlinePaymentsApiKey = !this.showWorldlinePaymentsApiKey;
  }

  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only JPG and PNG images are allowed.');
      input.value = '';
      return;
    }

    const maxSizeBytes = 1 * 1024 * 1024; // 1 MB
    if (file.size > maxSizeBytes) {
      alert('Image size must not exceed 1 MB.');
      input.value = '';
      return;
    }

    this.logoFile = file;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data URL prefix (e.g. "data:image/png;base64,") to get raw base64
      this.logoBase64 = result.split(',')[1];
      this.logoPreviewUrl = result;
      this.logoChanged = true;
      this.hasUnsavedChanges = true;
    };
    reader.readAsDataURL(file);
  }

  removeLogo(): void {
    this.logoFile = null;
    this.logoBase64 = null;
    this.logoPreviewUrl = null;
    this.logoChanged = true;
    this.hasUnsavedChanges = true;
  }

  private async saveOperatorLogo(): Promise<void> {
    const operatorId = this.appDataState.getOperatorId();
    const logoBytes = this.logoBase64
      ? Array.from(atob(this.logoBase64), c => c.charCodeAt(0))
      : null;
    await this.operatorService.update({
      id: operatorId,
      logo: logoBytes,
    });
    this.appDataState.setOperatorLogo(this.logoBase64);
  }
}
